import { ArrowRightIcon, SaveIcon, XIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import employersEmployees from 'rails_routes/Employers/EmployeesPaths'
import { Button, Tabs, useBreakpoint } from 'slab'
import { getISODateString } from 'utils'
import { usePostEmployersOffboardingBulkOffboard } from 'utils/web_api'

import { useBulkOffboarding } from './BulkOffboardingContext'
import { DATE_RANGE_YEARS_FUTURE, DATE_RANGE_YEARS_PAST } from './constants'
import { OffboardingFormFields } from './OffboardingFormFields'
import { OffboardingTimeline } from './OffboardingTimeline'

interface OffboardingDetailsStepProps {
  employerName?: string | null
  employerDba?: string | null
  isAle?: boolean | null
}

export function OffboardingDetailsStep ({ employerName, employerDba, isAle }: OffboardingDetailsStepProps) {
  const {
    currentEmployeeIndex,
    employeeOffboardingData,
    setEmployeeOffboardingData,
    getSelectedEmployees,
    goToNextEmployee,
    goToPreviousEmployee,
    setCurrentStep,
    isEditing,
  } = useBulkOffboarding()

  const bulkOffboardMutation = usePostEmployersOffboardingBulkOffboard()

  const selectedEmployees = getSelectedEmployees()
  const currentEmployee = selectedEmployees[currentEmployeeIndex]

  // Calculate min/max dates for validation
  const minDate = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - DATE_RANGE_YEARS_PAST)
    return getISODateString(date)
  }, [])
  const maxDate = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() + DATE_RANGE_YEARS_FUTURE)
    return getISODateString(date)
  }, [])

  // Track the last valid date for timeline display
  const [lastValidDate, setLastValidDate] = useState<string | null>(null)

  // Track COBRA confirmation checkbox for edit mode
  const [cobraConfirmationChecked, setCobraConfirmationChecked] = useState(false)

  const currentData = useMemo(() => {
    if (!currentEmployee) return null
    const existingData = employeeOffboardingData.get(currentEmployee.id)
    if (existingData) {
      return existingData
    }

    // Default values: use end_date or today for final day
    const defaultFinalDay = currentEmployee.end_date ?? getISODateString(new Date())
    return {
      employeeId: currentEmployee.id,
      finalDayOfEmployment: defaultFinalDay,
      cobraEligible: null,
      personalEmail: currentEmployee.personal_email ?? '',
    }
  }, [currentEmployee, employeeOffboardingData])

  useEffect(() => {
    if (currentData?.finalDayOfEmployment) {
      setLastValidDate(currentData.finalDayOfEmployment)
    }
  }, [currentData?.finalDayOfEmployment])

  const handleFieldChange = useCallback((field: string, value: string | boolean | null) => {
    if (!currentEmployee) return

    // If it's the finalDayOfEmployment field and it's a valid date, update lastValidDate
    if (field === 'finalDayOfEmployment' && typeof value === 'string' && value) {
      setLastValidDate(value)
    }

    // Reset COBRA confirmation when COBRA eligibility changes to false
    if (field === 'cobraEligible' && value === false) {
      setCobraConfirmationChecked(false)
    }

    setEmployeeOffboardingData(currentEmployee.id, { [field]: value })
  }, [currentEmployee, setEmployeeOffboardingData])

  // Manage COBRA confirmation checkbox state based on employee and their COBRA eligibility
  // In edit mode, pre-check if employee already has COBRA eligible (was previously confirmed)
  // Reset to unchecked when COBRA eligibility changes to false or null
  useEffect(() => {
    if (currentData?.cobraEligible === true && isEditing) {
      // In edit mode, pre-check since user already confirmed this before
      setCobraConfirmationChecked(true)
    } else if (!currentData?.cobraEligible) {
      setCobraConfirmationChecked(false)
    }
  }, [currentEmployee?.id, isEditing])

  const handleNext = useCallback(() => {
    goToNextEmployee()
  }, [goToNextEmployee])

  const handleBack = useCallback(() => {
    if (currentEmployeeIndex > 0) {
      goToPreviousEmployee()
    } else {
      setCurrentStep('select')
    }
  }, [currentEmployeeIndex, goToPreviousEmployee, setCurrentStep])

  const handleSaveChanges = useCallback(() => {
    if (!currentEmployee || !currentData) return

    const employees = [{
      employee_id: currentEmployee.id,
      effective_date: currentData.finalDayOfEmployment ?? '',
      cobra_eligible: currentData.cobraEligible ?? false,
      employee_personal_email: currentData.personalEmail ?? '',
    }]

    bulkOffboardMutation.mutate(
      { data: { employees } },
      {
        onSuccess: () => {
          window.location.href = employersEmployees.index()
        },
        onError: (error) => {
          console.error('Failed to save offboarding changes:', error)
          alert('Failed to save changes. Please try again.')
        },
      }
    )
  }, [currentEmployee, currentData, bulkOffboardMutation])

  const handleDiscard = useCallback(() => {
    window.location.href = employersEmployees.index()
  }, [])

  // Only show COBRA confirmation checkbox if in edit mode with COBRA eligible AND employer is not an ALE
  // ALEs (Applicable Large Employers with 50+ employees) don't need to confirm the 20-employee threshold
  const showCobraConfirmation = isEditing && currentData?.cobraEligible === true && !isAle

  const isFormValid = useMemo(() => {
    if (!currentData) return false
    const baseValid = (
      currentData.finalDayOfEmployment !== null &&
      currentData.cobraEligible !== null &&
      currentData.personalEmail.trim() !== ''
    )

    // In edit mode, if COBRA eligible, require confirmation
    if (showCobraConfirmation) {
      return baseValid && cobraConfirmationChecked
    }

    return baseValid
  }, [currentData, showCobraConfirmation, cobraConfirmationChecked])

  const { breakpointIsAtLeast } = useBreakpoint()
  const isDesktop = breakpointIsAtLeast('md')

  const displayEmployerName = employerDba || employerName || 'my organization'
  const currentYear = new Date().getFullYear()

  if (!currentEmployee || !currentData) {
    return (
      <div>
        <p className='text-gray-500'>No employee selected</p>
      </div>
    )
  }

  const formContent = (
    <div className='bg-white rounded-2xl border border-gray-200 shadow-sm'>
      <div className='flex flex-col gap-6 p-6'>
        <h2 className='body-bold'>Offboarding details</h2>

        <OffboardingFormFields
          employeeId={currentEmployee.id}
          finalDayOfEmployment={currentData.finalDayOfEmployment}
          cobraEligible={currentData.cobraEligible}
          personalEmail={currentData.personalEmail}
          onFieldChange={handleFieldChange}
          minDate={minDate}
          maxDate={maxDate}
          dateInputClassName={isDesktop ? 'w-1/2' : 'w-full'}
          showCobraConfirmation={showCobraConfirmation}
          cobraConfirmationChecked={cobraConfirmationChecked}
          onCobraConfirmationChange={setCobraConfirmationChecked}
          displayEmployerName={displayEmployerName}
          currentYear={currentYear}
        />
      </div>

      {/* Footer with divider */}
      <div className='border-t border-gray-200 p-6 flex flex-col gap-2'>
        {isEditing
          ? (
            <>
              <p className='body-sm text-gray-500 mb-.5'>
                Changes will be saved immediately and the employee's offboarding will be updated.
              </p>
              <Button
                variant='primary'
                size='large'
                width='full'
                disabled={!isFormValid || bulkOffboardMutation.isPending}
                onClick={handleSaveChanges}
                leftIcon={SaveIcon}
              >
                {bulkOffboardMutation.isPending ? 'Saving...' : 'Save changes'}
              </Button>
              <Button
                variant='secondary'
                size='large'
                width='full'
                onClick={handleDiscard}
                disabled={bulkOffboardMutation.isPending}
                leftIcon={XIcon}
              >
                Discard changes
              </Button>
            </>
            )
          : (
            <>
              <p className='body-sm text-gray-500 mb-.5'>
                Employees won't be offboarded until you submit at the end.
              </p>
              <Button
                variant='primary'
                size='large'
                width='full'
                disabled={!isFormValid}
                onClick={handleNext}
                rightIcon={ArrowRightIcon}
              >
                Next
              </Button>
              <Button
                variant='secondary'
                size='large'
                width='full'
                onClick={handleBack}
              >
                Back
              </Button>
            </>
            )}
      </div>
    </div>
  )

  const timelineContent = (
    <OffboardingTimeline
      employeeName={`${currentEmployee.first_name} ${currentEmployee.last_name}`}
      finalDayOfEmployment={currentData.finalDayOfEmployment || lastValidDate}
      cobraEligible={currentData.cobraEligible}
      willReceiveOffboardingEmail={currentEmployee.will_receive_offboarding_email}
    />
  )

  // Mobile layout with tabs
  if (!isDesktop) {
    return (
      <div>
        {/* Header - Employee name and email */}
        <div className='mb-4'>
          <h1 className='heading-md'>{currentEmployee.first_name} {currentEmployee.last_name}</h1>
          <p className='body-sm text-gray-500'>{currentEmployee.personal_email ?? 'No personal email on file'}</p>
        </div>

        {/* Card wrapper with tabs */}
        <div className='bg-white rounded-2xl border border-gray-200 shadow-sm'>
          <Tabs defaultTab='details'>
            <div className='pl-4 pt-2 shadow-[0_-1px_0_0_inset] shadow-gray-200'>
              <Tabs.List border={false}>
                <Tabs.Tab name='details'>Details</Tabs.Tab>
                <Tabs.Tab name='timeline'>Expected timeline</Tabs.Tab>
              </Tabs.List>
            </div>
            <Tabs.Panel name='details'>
              <div className='flex flex-col gap-6 p-6'>
                <h2 className='body-bold'>Offboarding details</h2>

                <OffboardingFormFields
                  employeeId={currentEmployee.id}
                  finalDayOfEmployment={currentData.finalDayOfEmployment}
                  cobraEligible={currentData.cobraEligible}
                  personalEmail={currentData.personalEmail}
                  onFieldChange={handleFieldChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  showCobraConfirmation={showCobraConfirmation}
                  cobraConfirmationChecked={cobraConfirmationChecked}
                  onCobraConfirmationChange={setCobraConfirmationChecked}
                  displayEmployerName={displayEmployerName}
                  currentYear={currentYear}
                />
              </div>

              {/* Footer with divider */}
              <div className='border-t border-gray-200 p-6 flex flex-col gap-4'>
                {isEditing
                  ? (
                    <>
                      <p className='body-sm text-gray-500'>
                        Changes will be saved immediately and the employee's offboarding will be updated.
                      </p>
                      <Button
                        variant='primary'
                        size='large'
                        width='full'
                        disabled={!isFormValid || bulkOffboardMutation.isPending}
                        onClick={handleSaveChanges}
                        leftIcon={SaveIcon}
                      >
                        {bulkOffboardMutation.isPending ? 'Saving...' : 'Save changes'}
                      </Button>
                      <Button
                        variant='secondary'
                        size='large'
                        width='full'
                        onClick={handleDiscard}
                        disabled={bulkOffboardMutation.isPending}
                        leftIcon={XIcon}
                      >
                        Discard changes
                      </Button>
                    </>
                    )
                  : (
                    <>
                      <p className='body-sm text-gray-500'>
                        Employees won't be offboarded until you submit at the end.
                      </p>
                      <Button
                        variant='primary'
                        size='large'
                        width='full'
                        disabled={!isFormValid}
                        onClick={handleNext}
                        rightIcon={ArrowRightIcon}
                      >
                        Next
                      </Button>
                      <Button
                        variant='secondary'
                        size='large'
                        width='full'
                        onClick={handleBack}
                      >
                        Back
                      </Button>
                    </>
                    )}
              </div>
            </Tabs.Panel>
            <Tabs.Panel name='timeline'>
              <div className='p-6'>
                {timelineContent}
              </div>
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    )
  }

  // Desktop layout with two columns
  return (
    <div className='flex gap-10'>
      {/* Left column - Form */}
      <div className='flex-1'>
        {/* Header - Employee name and email */}
        <div className='mb-6'>
          <h1 className='heading-md'>{currentEmployee.first_name} {currentEmployee.last_name}</h1>
          <p className='body-sm text-gray-500'>{currentEmployee.personal_email ?? 'No personal email on file'}</p>
        </div>

        {formContent}
      </div>

      {/* Right column - Timeline (aligned with form card header) */}
      <div className='flex-1'>
        {/* Spacer to align with form card: header section (mb-6) + card padding (p-6) */}
        <div className='mb-6 invisible'>
          <p className='heading-md'>&nbsp;</p>
          <p className='body-sm'>&nbsp;</p>
        </div>
        <div className='pt-6'>
          {timelineContent}
        </div>
      </div>
    </div>
  )
}
